import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CircleCheckIcon,
  FlaskConicalIcon,
  MessageSquareIcon,
  TagIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { Skeleton } from '@/components/ui/skeleton';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { cn } from '@/lib/utils';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';
import { formatDate } from '@/util/dateUtils';
import { ratioToPercent } from '@/util/numbers';

import { columns as evaluationColumns } from './components/RunDetails/EvaluationsTable/columns';
import { EvaluationsTable } from './components/RunDetails/EvaluationsTable/EvaluationsTable';
import { SimulationPreviewDialog } from './components/RunDetails/SimulationPreviewDialog/SimulationPreviewDialog';
import { useEvaluationsFilters } from './hooks/useEvaluationsFilters';
import { useSimulationRun, useEvaluations } from './hooks/useSimulations';
import { categoryToFilterValue } from './hooks/useSimulationsFilters';
import { useSimulationsBreadcrumb } from './SimulationsBreadcrumbContext';
import {
  CHANNEL_LABEL_KEYS,
  EVALUATION_STATUS_TRANSLATION_KEYS,
  RESULT_STATUSES,
  SIMULATION_CHANNEL_TYPES,
  type EvaluationWithSimulation,
  type SimulationRun,
} from './types';
import { listCategories } from './utils';

type StatCardContext = { run: SimulationRun | undefined; successRate: number };

const STAT_CARDS: {
  id: string;
  Icon: typeof CircleCheckIcon;
  iconClassName: string;
  labelKey: string;
  getValue: (ctx: StatCardContext) => string | number;
}[] = [
  {
    id: 'success_rate',
    Icon: CircleCheckIcon,
    iconClassName: 'text-success',
    labelKey: 'simulations.success_rate',
    getValue: ({ successRate }) => `${successRate}%`,
  },
  {
    id: 'failed',
    Icon: TriangleAlertIcon,
    iconClassName: 'text-warning',
    labelKey: 'simulations.failed',
    getValue: ({ run }) => run?.fail_count ?? 0,
  },
  {
    id: 'simulations',
    Icon: FlaskConicalIcon,
    iconClassName: 'text-primary',
    labelKey: 'simulations.simulations_count',
    getValue: ({ run }) => run?.total_count ?? 0,
  },
];

export const RunDetailsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const { runId, simulationId } = useParams<{ runId: string; simulationId?: string }>();

  const { run, isLoading: isRunLoading, error: runError } = useSimulationRun(brainId, runId);
  const { evaluations, isLoading: isEvalsLoading } = useEvaluations(brainId, runId);
  const { setRunLabel } = useSimulationsBreadcrumb();

  // Close any open sidebar when navigating to run details
  useEffect(() => {
    dispatch(setSidebarContent(null));
  }, [dispatch]);

  useSegments(PageName.SIMULATION_RUN, runId);

  useEffect(() => {
    if (runError?.statusCode === 404) {
      toast.info(t('simulations.run_unavailable'));
      navigate(`/${slug}/ai-agents/${brainId}/simulations/overview`, { replace: true });
    }
  }, [runError, navigate, slug, brainId, t]);

  useEffect(() => {
    if (run?.started_at) {
      setRunLabel(formatDate(run.started_at, 'MMM d, h:mm a'));
    }
    return () => setRunLabel(null);
  }, [run?.started_at, setRunLabel]);

  const [search, setSearch] = useState('');
  const [openPillId, setOpenPillId] = useState<string | null>(null);
  // Resolve the previewed evaluation from the URL so the dialog is deep-linkable
  // and reflects status transitions (running → pass / fail / error) via polling.
  const previewEval = useMemo(
    () => evaluations?.find((e) => e.simulation_id === simulationId) ?? null,
    [evaluations, simulationId]
  );

  // Absolute, anchored to the run: a relative href would nest under an open :simulationId and 404.
  const getRowHref = useCallback(
    (evaluation: EvaluationWithSimulation) =>
      `/${slug}/ai-agents/${brainId}/simulations/runs/${runId}/${evaluation.simulation_id}`,
    [slug, brainId, runId]
  );

  const handlePreview = useCallback(
    (evaluation: EvaluationWithSimulation) => {
      navigate(getRowHref(evaluation));
    },
    [navigate, getRowHref]
  );

  const handlePreviewOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        navigate('..', { relative: 'path' });
      }
    },
    [navigate]
  );

  const { filters, activeFilterCount, setFilter, removeFilter, resetFilters, filteredEvaluations } =
    useEvaluationsFilters(evaluations ?? []);

  const categories = useMemo(
    () => listCategories((evaluations ?? []).map((e) => e.simulation)),
    [evaluations]
  );

  const filterDefinitions = useMemo((): FilterDefinition[] => {
    const uncategorizedSentinel = categoryToFilterValue(null);
    const categoryOptions = [
      ...categories
        .filter((c): c is string => c !== null)
        .map((c) => ({ label: c, value: categoryToFilterValue(c) })),
      ...(categories.includes(null)
        ? [{ label: t('simulations.category_uncategorized'), value: uncategorizedSentinel }]
        : []),
    ];

    const resultOptions = RESULT_STATUSES.map((s) => ({
      label: t(EVALUATION_STATUS_TRANSLATION_KEYS[s]),
      value: s,
    }));

    const channelOptions = SIMULATION_CHANNEL_TYPES.map((ch) => ({
      label: t(CHANNEL_LABEL_KEYS[ch]),
      value: ch,
    }));

    return [
      {
        id: 'status',
        label: t('simulations.filter_results'),
        icon: CircleCheckIcon,
        type: 'checkbox',
        options: resultOptions,
      },
      {
        id: 'channel_type',
        label: t('simulations.filter_channel'),
        icon: MessageSquareIcon,
        type: 'checkbox',
        options: channelOptions,
      },
      {
        id: 'category',
        label: t('simulations.filter_category'),
        icon: TagIcon,
        type: 'checkbox',
        options: categoryOptions,
      },
    ];
  }, [categories, t]);

  const filterBadges = useMemo(
    () => buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter }),
    [filterDefinitions, filters, setFilter, removeFilter]
  );

  const displayedEvaluations = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) {
      return filteredEvaluations;
    }
    return filteredEvaluations.filter((e) => e.simulation.name.toLowerCase().includes(lower));
  }, [filteredEvaluations, search]);

  const tableColumns = useMemo(
    () => evaluationColumns({ onPreview: handlePreview }),
    [handlePreview]
  );
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  } = useColumnVisibility(tableColumns, 'evaluationsColumnVisibility');

  const successRate = ratioToPercent(run?.success_rate ?? null);

  return (
    <div className={cn(TABLE_LAYOUT_FILL_CLASSES, 'gap-6')}>
      <div className="grid grid-cols-3 gap-4">
        {STAT_CARDS.map(({ id, Icon, iconClassName, labelKey, getValue }) => (
          <div
            key={id}
            className="flex items-center gap-4 rounded-lg border border-border bg-background p-3"
          >
            <div className="flex shrink-0 items-center justify-center rounded-md border border-border bg-background p-1.5">
              <Icon size={16} className={iconClassName} />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">{t(labelKey)}</p>
              {run ? (
                <p className="text-2xl font-semibold tracking-tight text-foreground">
                  {getValue({ run, successRate })}
                </p>
              ) : (
                <Skeleton className="h-8 w-16" />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex h-10 items-center justify-between gap-2 pl-1">
          <SearchBar
            placeholder={t('simulations.search_evaluations')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <ComplexFilterCombobox
              filterDefinitions={filterDefinitions}
              filters={filters}
              setFilter={setFilter}
              removeFilter={removeFilter}
              activeFilterCount={activeFilterCount}
              onOpenActivePill={setOpenPillId}
            />
            <ColumnVisibilityMenu
              columns={hideableColumns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              onReset={resetColumnVisibility}
              open={isCustomizeColumnsOpen}
              onOpenChange={setIsCustomizeColumnsOpen}
              onInteractOutside={(e) => {
                if (e.target === customizeButtonRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </div>
        </div>
        <ComplexFilterBar
          filters={filterBadges}
          openPillId={openPillId}
          onOpenPillChange={setOpenPillId}
          onClearAll={resetFilters}
        />
        <EvaluationsTable
          evaluations={displayedEvaluations}
          columns={tableColumns}
          isLoading={isRunLoading || isEvalsLoading}
          getRowHref={getRowHref}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          paginationCenterContent={
            hasHiddenColumns ? (
              <HiddenColumnsIndicator
                onCustomize={() => setIsCustomizeColumnsOpen((prev) => !prev)}
                buttonRef={customizeButtonRef}
              />
            ) : undefined
          }
        />
      </div>

      {previewEval && (
        <SimulationPreviewDialog
          key={previewEval.evaluation_id}
          evaluation={previewEval}
          open
          onOpenChange={handlePreviewOpenChange}
        />
      )}
    </div>
  );
};
